import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Table, TableBody, TableCell, TableHead, TableRow,
    Typography, Paper, Stack, Grid, MenuItem, CircularProgress,
    IconButton, Card, InputAdornment, TableContainer, Divider
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
    setDoc, doc, getDocs, collection, updateDoc, deleteDoc,
    query, orderBy, where, getDoc, writeBatch
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import {
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    Add as AddIcon,
    School as SchoolIcon
} from '@mui/icons-material';

const Subjectdef = () => {
    const uid = auth.currentUser?.uid;
    const [schoolId, setSchoolId] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [courseOptions, setCourseOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Dialog States
    const [open, setOpen] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        course_code: '', medium: '', semester: '', sub_code: '',
        sub_name: '', type1: '', type2: '', schoolId: ''
    });

    const [copyData, setCopyData] = useState({ fromCourse: '', toCourse: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        const init = async () => {
            if (!uid) return;
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
                const id = userDoc.data().schoolId;
                setSchoolId(id);
                setFormData(prev => ({ ...prev, schoolId: id }));
            }
        };
        init();
    }, [uid]);

    useEffect(() => {
        if (schoolId) {
            fetchCourseOptions();
            fetchSubjects();
        }
    }, [schoolId]);

    const fetchCourseOptions = async () => {
        const q = query(collection(db, 'courses'), where('schoolId', '==', schoolId), orderBy('sequence', 'asc'));
        const snap = await getDocs(q);
        setCourseOptions(snap.docs.map(doc => doc.data().name));
    };

    const fetchSubjects = async () => {
        setLoading(true);
        const q = query(collection(db, 'subjects'), where('schoolId', '==', schoolId));
        const snap = await getDocs(q);
        setSubjects(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
    };

    // --- Logic to Group Subjects by Course ---
    const groupedData = useMemo(() => {
        const filtered = subjects.filter(s =>
            (s.sub_code + s.sub_name + s.course_code).toLowerCase().includes(searchTerm.toLowerCase())
        );

        return filtered.reduce((acc, curr) => {
            const group = curr.course_code || 'Unassigned';
            if (!acc[group]) acc[group] = [];
            acc[group].push(curr);
            return acc;
        }, {});
    }, [subjects, searchTerm]);

    // 2. Create the ordered sequence based on courseOptions
    // This ensures Nursery comes before LKG, etc.
    const orderedGroups = useMemo(() => {
        const sortedKeys = [...courseOptions];
        // Add "Unassigned" to the end if there are subjects without a course
        if (groupedData['Unassigned']) sortedKeys.push('Unassigned');
        return sortedKeys;
    }, [courseOptions, groupedData]);

    const handleSubmit = async () => {
        if (!formData.course_code || !formData.sub_code || !formData.sub_name) {
            return toast.error("Fill required fields");
        }
        setLoading(true);
        try {
            if (editMode && editId) {
                await updateDoc(doc(db, "subjects", editId), { ...formData, updatedAt: new Date() });
                toast.success("Updated");
            } else {
                const id = uuidv4();
                await setDoc(doc(db, "subjects", id), { ...formData, id, createdAt: new Date(), schoolId });
                toast.success("Added");
            }
            handleClose();
            fetchSubjects();
        } catch (err) { toast.error(err.message); }
        setLoading(false);
    };

    const handleCopySubjects = async () => {
        if (!copyData.fromCourse || !copyData.toCourse) return toast.error("Select both courses");
        setLoading(true);
        try {
            const sourceSubs = subjects.filter(s => s.course_code === copyData.fromCourse);
            const batch = writeBatch(db);
            sourceSubs.forEach(sub => {
                const newId = uuidv4();
                const { id, ...data } = sub;
                batch.set(doc(db, "subjects", newId), { ...data, id: newId, course_code: copyData.toCourse, createdAt: new Date() });
            });
            await batch.commit();
            toast.success("Copied successfully");
            setCopyDialogOpen(false);
            fetchSubjects();
        } catch (err) { toast.error(err.message); }
        setLoading(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditMode(false);
        setFormData({ course_code: '', medium: '', semester: '', sub_code: '', sub_name: '', type1: '', type2: '', schoolId });
    };

    return (
        <Box p={1} sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header Section */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">Subject Allocation</Typography>
                    <Typography variant="caption" color="textSecondary">Grouped by Class/Standard</Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<CopyIcon />} onClick={() => setCopyDialogOpen(true)}>Copy Setup</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add Subject</Button>
                </Stack>
            </Stack>

            {/* Search Bar */}
            <Card sx={{ mb: 4, p: 2, borderRadius: 2 }}>
                <TextField
                    fullWidth size="small" placeholder="Search across all classes..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} /> }}
                />
            </Card>

            {loading && <CircularProgress sx={{ display: 'block', mx: 'auto' }} />}

            <Box p={1} sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
                {/* Header and Search Bar remain the same */}

                {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}

                {/* Render based on the ORDERED sequence */}
                {orderedGroups.map((course) => {
                    const courseSubjects = groupedData[course];

                    // Only render the table if there are subjects for this course
                    if (!courseSubjects || courseSubjects.length === 0) return null;

                    return (
                        <Box key={course} mb={4}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <SchoolIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle1" fontWeight="800" sx={{ textTransform: 'uppercase' }}>
                                    {course}
                                    <span style={{ color: 'gray', fontWeight: 400, marginLeft: '8px' }}>
                                        ({courseSubjects.length} Subjects)
                                    </span>
                                </Typography>
                            </Stack>

                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f0f4f8' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Code</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Subject Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Medium</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {courseSubjects.map((s) => (
                                            <TableRow key={s.id} hover>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{s.sub_code}</TableCell>
                                                <TableCell>{s.sub_name}</TableCell>
                                                <TableCell>{s.medium || 'N/A'}</TableCell>
                                                <TableCell>{s.type1}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" color="primary" onClick={() => { setEditMode(true); setEditId(s.id); setFormData(s); setOpen(true); }}>
                                                        <EditIcon fontSize="inherit" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete?')) { await deleteDoc(doc(db, 'subjects', s.id)); fetchSubjects(); } }}>
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    );
                })}

                {/* Modals remain the same */}
            </Box>

            {/* MODALS (Add/Edit & Copy) remain same as previous step for functionality */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editMode ? 'Edit' : 'Add'} Subject</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} pt={1}>
                        <Grid className="col-sm-6 mb-2">
                            <TextField select fullWidth size="small" label="Course" name="course_code" value={formData.course_code} onChange={handleChange}>
                                {courseOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-6 mb-2"><TextField fullWidth size="small" label="Sub Code" name="sub_code" value={formData.sub_code} onChange={handleChange} /></Grid>
                        <Grid className="col-sm-6 mb-2"><TextField fullWidth size="small" label="Sub Name" name="sub_name" value={formData.sub_name} onChange={handleChange} /></Grid>
                        <Grid className="col-sm-6 mb-2"><TextField fullWidth size="small" label="Medium" name="medium" value={formData.medium} onChange={handleChange} /></Grid>
                        <Grid className="col-sm-6 mb-2"><TextField fullWidth size="small" label="Semester" name="semester" value={formData.semester} onChange={handleChange} /></Grid>
                        <Grid className="col-sm-6 mb-2"><TextField fullWidth size="small" label="Type" name="type1" value={formData.type1} onChange={handleChange} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save Subject</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Copy Subjects</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} pt={1}>
                        <TextField select fullWidth size="small" label="From Course" value={copyData.fromCourse} onChange={(e) => setCopyData({ ...copyData, fromCourse: e.target.value })}>
                            {courseOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </TextField>
                        <TextField select fullWidth size="small" label="To Course" value={copyData.toCourse} onChange={(e) => setCopyData({ ...copyData, toCourse: e.target.value })}>
                            {courseOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCopySubjects}>Start Batch Copy</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Subjectdef;